import React, { useMemo, useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faNotesMedical, faCircleCheck, faTriangleExclamation, faFileMedical,
  faLayerGroup, faGaugeHigh, faChartLine, faMagnifyingGlass, faDownload, faRotate,
  faFilePdf, faFileExcel, faFileCsv, faXmark, faChevronRight, faCheck, faWandMagicSparkles,
  faSort, faSortUp, faSortDown, faFilter, faCircleInfo, faPrint, faLocationDot, faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { faFile } from '@fortawesome/free-regular-svg-icons'
import { toast } from 'react-toastify';
import EncounterTrackerTableV2 from '../EncounterTrackerTableV2';
import TrackerChartV2 from '../TrackerChart';
import NameCell from './NameCell';
import './dashboard.css';
import {
  getInitials, getDisplayName, getRoleBadge, getAvatarColor,
  formatNumber, formatPercent, computeKpis, completionTone, computeInsights, rowTotalEnc,
} from './helpers';

/* ── small utilities ──────────────────────────────────────────────────── */
const num = (v) => Number(v) || 0;

const downloadCsv = (filename, rows) => {
  const csv = rows.map((r) => r.map((c) => {
    const s = String(c ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

/* ── KPI grid ─────────────────────────────────────────────────────────── */
const KPI_DEFS = [
  { key: 'totalPatients', label: 'Total Patients', icon: faUsers, tone: 'teal', tip: 'Distinct patients across selected staff.' },
  { key: 'totalEncounters', label: 'Total Encounters', icon: faNotesMedical, tone: 'teal', tip: 'Signed + unsigned encounters.' },
  { key: 'signed', label: 'Signed Encounters', icon: faCircleCheck, tone: 'success', tip: 'Encounters signed off.' },
  { key: 'unsigned', label: 'Unsigned Encounters', icon: faTriangleExclamation, tone: 'warning', tip: 'Encounters awaiting signature.' },
  { key: 'totalCPTs', label: 'Total CPTs', icon: faFileMedical, tone: 'orange', tip: 'Total CPT codes billed.' },
  { key: 'totalUnits', label: 'Total Units', icon: faLayerGroup, tone: 'slate', tip: 'Total billed units.' },
  { key: 'completionRate', label: 'Completion Rate', icon: faGaugeHigh, tone: 'dynamic', pct: true, tip: 'Signed ÷ total encounters.' },
  { key: 'avgUnits', label: 'Avg Units / Provider', icon: faChartLine, tone: 'slate', dec: true, tip: 'Total units ÷ staff count.' },
];

const KpiGrid = ({ kpis, activeMetric, onPick }) => (
  <div className="ptd-kpis">
    {KPI_DEFS.map((d) => {
      const tone = d.tone === 'dynamic' ? completionTone(kpis.completionRate) : d.tone;
      const raw = kpis[d.key];
      const value = d.pct ? formatPercent(raw, 0) : d.dec ? num(raw).toFixed(1) : formatNumber(raw);
      return (
        <button
          type="button"
          key={d.key}
          className={`ptd-kpi tone-${tone} ${activeMetric === d.key ? 'is-active' : ''}`}
          onClick={() => onPick && onPick(d.key)}
        >
          <span className="ptd-kpi-top">
            <span className="ptd-kpi-icon"><FontAwesomeIcon icon={d.icon} /></span>
            <Tooltip title={d.tip} arrow><span className="ptd-kpi-info"><FontAwesomeIcon icon={faCircleInfo} /></span></Tooltip>
          </span>
          <span className="ptd-kpi-value">{value}</span>
          <span className="ptd-kpi-label">{d.label}</span>
        </button>
      );
    })}
  </div>
);

/* ── Staff selector ───────────────────────────────────────────────────── */
const StaffSelector = ({
  roleLabel, providerData, selectedProviders, setSelectedProviders, handleCheckboxChange,
  locationData, selectedLocationID, onLocationChange,
}) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);          // typeahead dropdown visibility
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const comboRef = useRef(null);

  const allIds = useMemo(() => (providerData || []).map((p) => p.ProviderID), [providerData]);

  // Matches shown in the typeahead dropdown — only while there's a search term.
  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return (providerData || []).filter((p) => getDisplayName(p.ProviderName).toLowerCase().includes(term));
  }, [providerData, q]);

  // Selected provider objects, for the chip row.
  const selectedList = useMemo(
    () => (providerData || []).filter((p) => selectedProviders.includes(p.ProviderID)),
    [providerData, selectedProviders]
  );

  // Close the dropdown when clicking outside the combo box.
  useEffect(() => {
    const onDoc = (e) => { if (comboRef.current && !comboRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const locations = locationData || [];
  const MAX_CHIPS = 8;
  const shownChips = chipsExpanded ? selectedList : selectedList.slice(0, MAX_CHIPS);
  const extraChips = selectedList.length - MAX_CHIPS;

  return (
    <section className="hv-panel ptd-card ptd-staff">
      <header className="hv-panel__head ptd-card-head">
        <div>
          <h3>{roleLabel}s</h3>
          <span className="ptd-muted">{selectedProviders.length}/{allIds.length} selected</span>
        </div>
      </header>

      {/* Location filter — refetches the tracker data on the server for the
          chosen location (sends sLocationID to the report APIs). */}
      <div className="ptd-staff-loc-row">
        <label htmlFor="ptd-loc"><FontAwesomeIcon icon={faLocationDot} /> Location</label>
        <select
          id="ptd-loc"
          className="ptd-staff-loc"
          value={selectedLocationID ?? '-1'}
          onChange={(e) => onLocationChange && onLocationChange(e.target.value)}
        >
          <option value="-1">All locations</option>
          {locations.map((l) => (
            <option key={l.LocationId} value={l.LocationId}>
              {l.LocationNickname || l.LocationName || `Location ${l.LocationId}`}
            </option>
          ))}
        </select>
      </div>

      {/* Type-to-add combo box — the list stays hidden until you search, so the
          panel stays short no matter how many providers exist. */}
      <div className="ptd-staff-combo" ref={comboRef}>
        <div className="ptd-staff-search">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={`Search ${roleLabel.toLowerCase()} to add…`}
          />
          {q && (
            <button type="button" className="ptd-staff-clearq" onClick={() => setQ('')} aria-label="Clear search">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>

        {open && q.trim() && (
          <div className="ptd-staff-dropdown">
            {matches.length === 0 ? (
              <div className="ptd-empty-mini">No {roleLabel.toLowerCase()}s match “{q.trim()}”.</div>
            ) : matches.map((p) => {
              const sel = selectedProviders.includes(p.ProviderID);
              return (
                <button
                  type="button"
                  key={p.ProviderID}
                  className={`ptd-staff-opt ${sel ? 'is-selected' : ''}`}
                  onClick={() => handleCheckboxChange(p.ProviderID)}
                >
                  <span className="ptd-avatar sm" style={{ background: getAvatarColor(p.ProviderName) }}>{getInitials(p.ProviderName)}</span>
                  <span className="ptd-staff-meta">
                    <span className="ptd-staff-name">{getDisplayName(p.ProviderName)}</span>
                    <span className="ptd-badge">{getRoleBadge(p.ProviderName, roleLabel)}</span>
                  </span>
                  <span className={`ptd-check ${sel ? 'on' : ''}`}><FontAwesomeIcon icon={sel ? faCheck : faPlus} /></span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="ptd-staff-actions">
        <button type="button" onClick={() => setSelectedProviders(allIds)}>Select all</button>
        <button type="button" onClick={() => setSelectedProviders([])}>Clear all</button>
      </div>

      {/* Selected providers as compact removable chips. */}
      <div className="ptd-staff-chips">
        {selectedList.length === 0 ? (
          <span className="ptd-empty-mini">No {roleLabel.toLowerCase()}s selected — search above to add.</span>
        ) : (
          <>
            {shownChips.map((p) => (
              <span key={p.ProviderID} className="ptd-staff-chip" title={getDisplayName(p.ProviderName)}>
                <span className="ptd-staff-chip-av" style={{ background: getAvatarColor(p.ProviderName) }}>{getInitials(p.ProviderName)}</span>
                <span className="ptd-staff-chip-name">{getDisplayName(p.ProviderName)}</span>
                <button type="button" onClick={() => handleCheckboxChange(p.ProviderID)} aria-label={`Remove ${getDisplayName(p.ProviderName)}`}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            ))}
            {extraChips > 0 && (
              <button type="button" className="ptd-staff-chip more" onClick={() => setChipsExpanded((v) => !v)}>
                {chipsExpanded ? 'Show less' : `+${extraChips} more`}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
};

/* ── Insights ─────────────────────────────────────────────────────────── */
const InsightsPanel = ({ rows, roleLabel }) => {
  const ins = useMemo(() => computeInsights(rows), [rows]);
  const tone = completionTone(ins.kpis.completionRate);
  const aiSummary = () => {
    const top = ins.topProvider ? getDisplayName(ins.topProvider.ProviderName) : '—';
    toast.info(`${roleLabel} summary: ${formatNumber(ins.kpis.totalEncounters)} encounters, ${formatPercent(ins.kpis.completionRate)} signed. Top: ${top}.`, { autoClose: 5000 });
  };
  return (
    <section className="hv-panel ptd-card ptd-insights">
      <header className="hv-panel__head ptd-card-head">
        <div><h3>Insights</h3><span className="ptd-muted">Productivity highlights</span></div>
        {/* <button type="button" className="ptd-ai-btn" onClick={aiSummary}>
          <FontAwesomeIcon icon={faWandMagicSparkles} /> AI Summary
        </button> */}
      </header>
      <ul className="ptd-insight-list">
        <li>
          <span className="ptd-insight-ico tone-teal"><FontAwesomeIcon icon={faChartLine} /></span>
          <div><strong>Top {roleLabel.toLowerCase()}</strong>
            <p>{ins.topProvider ? `${getDisplayName(ins.topProvider.ProviderName)} — ${formatNumber(ins.topProviderEnc)} encounters` : 'No data yet'}</p>
          </div>
        </li>
        <li>
          <span className={`ptd-insight-ico tone-${ins.unsignedTotal > 0 ? 'warning' : 'success'}`}><FontAwesomeIcon icon={faTriangleExclamation} /></span>
          <div><strong>Unsigned encounters</strong>
            <p>{ins.unsignedTotal > 0 ? `${formatNumber(ins.unsignedTotal)} unsigned across ${ins.unsignedCount} ${roleLabel.toLowerCase()}(s)` : 'All encounters signed 🎉'}</p>
          </div>
        </li>
        <li>
          <span className={`ptd-insight-ico tone-${tone}`}><FontAwesomeIcon icon={faGaugeHigh} /></span>
          <div><strong>Completion rate</strong>
            <p>{formatPercent(ins.kpis.completionRate)} of encounters signed off</p>
          </div>
        </li>
      </ul>
    </section>
  );
};

/* ── Grouped grid (CPT / Units) ───────────────────────────────────────── */
const GroupedGrid = React.forwardRef(({ groups, codes, rows, totalsSource, accent, search, onSearch, onSort, sortConfig }, ref) => {
  const sortIcon = (code) => (sortConfig?.key !== code ? faSort : sortConfig.ascending ? faSortUp : faSortDown);
  return (
    <div className="hv-table-scroll ptd-grid-wrap" ref={ref}>
      <table className={`hv-table hv-table--compact hv-table--striped ptd-grid accent-${accent}`}>
        <thead>
          <tr>
            <th className="ptd-grid-idx" rowSpan={2}>#</th>
            <th className="ptd-grid-name" rowSpan={2}>
              Name
              <input className="ptd-grid-search" value={search} onChange={onSearch} placeholder="Search name" />
            </th>
            {groups.map((g, i) => (
              <th key={i} colSpan={g.cpts.length} className="ptd-grid-group">{g.service}</th>
            ))}
            <th className="ptd-grid-total" rowSpan={2}>Total</th>
          </tr>
          <tr>
            {codes.map((code, i) => (
              <th key={i} className="ptd-grid-code">
                <button type="button" onClick={() => onSort(code)}>{code} <FontAwesomeIcon icon={sortIcon(code)} /></button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={codes.length + 3} className="ptd-grid-empty">No data available</td></tr>
          ) : rows.map((item, idx) => {
            const rowTotal = codes.reduce((s, c) => s + num(item[c]), 0);
            return (
              <tr key={idx}>
                <td className="ptd-grid-idx">{idx + 1}</td>
                <td className="ptd-grid-name"><NameCell name={item.ProviderName} /></td>
                {codes.map((c, i) => <td key={i} className="ptd-num">{item[c] ? item[c] : 0}</td>)}
                <td className="ptd-num ptd-grid-rowtotal">{rowTotal}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td className="ptd-grid-idx">Σ</td>
            <td className="ptd-grid-name">Total</td>
            {codes.map((c, i) => (
              <td key={i} className="ptd-num">{(totalsSource || []).reduce((s, r) => s + num(r[c]), 0)}</td>
            ))}
            <td className="ptd-num ptd-grid-rowtotal">
              {(totalsSource || []).reduce((s, r) => s + codes.reduce((ss, c) => ss + num(r[c]), 0), 0)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
});

/* ── States ───────────────────────────────────────────────────────────── */
const TableSkeleton = () => (
  <div className="ptd-skeleton">
    {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} variant="rounded" height={34} sx={{ my: 0.6 }} />)}
  </div>
);
const EmptyState = ({ label }) => (
  <div className="ptd-empty">
    <FontAwesomeIcon icon={faMagnifyingGlass} />
    <h4>No {label} data</h4>
    <p>Adjust the date range or staff selection, then run the report.</p>
  </div>
);

/* ── Main dashboard ───────────────────────────────────────────────────── */
const REPORT_TABS = [
  { key: 'encounter', label: 'Encounter Tracker' },
  { key: 'cpt', label: 'CPT Tracker' },
  { key: 'units', label: 'Units Tracker' },
  { key: 'chart', label: 'Chart' },
];

const TrackerDashboardV2 = ({ ctx }) => {
  const {
    roleLabel = 'Provider',
    startDate, endDate, loading, setStartDate, setEndDate,
    handleStartDateChange, handleEndDateChange, onApply,
    searchQuery, handleSearchChange, setSearchQuery,
    providerData, selectedProviders, setSelectedProviders, handleCheckboxChange,
    locationData, selectedLocationID, setSelectedLocationID,
    filteredTable1,
    handleGetPatientTrackerDetailedData, handleGetUserAudits, encounterTableRef,
    groupedCPTs, cptCodes, sortedTable2, CptTrackerData, searchQueryTable2, handleTable2SearchChange, sortConfigTable2, sortedTable2Ref,
    groupedunits, unitCodes, sortedTable3, unitTrackerTable1, searchQueryTable3, handleTable3SearchChange, sortConfigTable3, sortedTable3Ref,
    handleSort,
    chartData, formattedStartDate, formattedEndDate,
    exportEncToExcel, exportToExcel, exportToPDF,
    showDataModal, showEncModal, showAuditModal,
    filteredEncData, filteredEncPatData, filteredAuditData,
    handleCloseModals, handleAuditCloseModals,
    handlePatLinkClick, handleGetEncounterAudits, getSignedOffMsg, formatDOSDate, patientTableRef,
  } = ctx;

  const [report, setReport] = useState('encounter');
  const [activeMetric, setActiveMetric] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [rowDrawer, setRowDrawer] = useState(null);

  const kpis = useMemo(() => computeKpis(filteredTable1), [filteredTable1]);
  const hasRows = (filteredTable1 || []).length > 0;
  const dateLabel = `${formattedStartDate} – ${formattedEndDate}`;
  const breakdownOpen = showDataModal || showEncModal;

  // Reset restores the default date range (previous month — same as initial
  // load), clears the search, re-selects all staff, then reloads. The reapply
  // flag defers onApply() until AFTER the new dates are committed to state, so
  // the refetch uses the reset range (not the stale one).
  const [reapply, setReapply] = useState(false);
  useEffect(() => {
    if (reapply) { onApply(); setReapply(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reapply, startDate, endDate]);

  const handleReset = () => {
    const today = new Date();
    const fmt = (d) => d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    setStartDate(fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1)));
    setEndDate(fmt(new Date(today.getFullYear(), today.getMonth(), 0)));
    setSearchQuery('');
    setSelectedProviders((providerData || []).map((p) => p.ProviderID));
    setReapply(true);
  };

  const pickMetric = (key) => {
    setActiveMetric((m) => (m === key ? null : key));
    if (['signed', 'unsigned', 'totalEncounters', 'totalPatients'].includes(key)) setReport('encounter');
    if (key === 'totalCPTs') setReport('cpt');
    if (key === 'totalUnits' || key === 'avgUnits') setReport('units');
    if (key === 'completionRate') setReport('chart');
  };

  const exportActive = (kind) => {
    setExportOpen(false);
    const map = { encounter: ['encounterTable', 'EncounterTracker'], cpt: ['cptTrackerTable', 'CptTracker'], units: ['unitTrackerTable', 'UnitsTracker'] };
    const target = map[report] || map.encounter;
    if (kind === 'pdf') return exportToPDF(target[0], target[1]);
    if (kind === 'excel') return report === 'encounter' ? exportEncToExcel(target[0], target[1]) : exportToExcel(target[0], target[1]);
    // CSV (client-side, no API)
    if (report === 'encounter') {
      const head = ['Name', 'Total Patients', 'Signed', 'Unsigned', 'Total Enc', 'Total CPTs', 'Total Units'];
      const body = (filteredTable1 || []).map((r) => [getDisplayName(r.ProviderName), num(r.nonduplicatePatcount), num(r.SignedEnc), num(r.UnsignedEnc), rowTotalEnc(r), num(r.CPTCount), num(r.UnitCount)]);
      downloadCsv('encounter-tracker.csv', [head, ...body]);
    } else {
      const codes = report === 'cpt' ? cptCodes : unitCodes;
      const rows = report === 'cpt' ? sortedTable2 : sortedTable3;
      const head = ['Name', ...codes, 'Total'];
      const body = rows.map((r) => [getDisplayName(r.ProviderName), ...codes.map((c) => num(r[c])), codes.reduce((s, c) => s + num(r[c]), 0)]);
      downloadCsv(`${report}-tracker.csv`, [head, ...body]);
    }
    toast.success('CSV downloaded');
  };

  // Print the active report table in a clean, print-optimised window. Reuses the
  // same table refs the page already exposes (so no API / data changes), strips
  // search inputs + icons, and adds a title + date range header.
  const printActive = () => {
    const refMap = {
      encounter: [encounterTableRef, 'Encounter Tracker'],
      cpt: [sortedTable2Ref, 'CPT Tracker'],
      units: [sortedTable3Ref, 'Units Tracker'],
    };
    const [ref, title] = refMap[report] || refMap.encounter;
    const node = ref && ref.current;
    if (!node || !node.innerHTML) { toast.error('Nothing to print yet'); return; }
    const win = window.open('', '_blank');
    if (!win) { toast.error('Allow pop-ups to print this report'); return; }
    win.document.write(
      '<!doctype html><html><head><title>' + title + '</title><style>'
      + '@media print { @page { size: A4 landscape; margin: 12mm; } }'
      + "body { font-family: 'Segoe UI', Arial, sans-serif; margin: 24px; color: #0F172A; }"
      + 'h1 { font-size: 20px; margin: 0 0 4px; }'
      + '.sub { font-size: 13px; color: #64748B; margin: 0 0 18px; }'
      + 'input, .ptd-grid-search, .ptv2-mt-search, .ptv2-mt-audit, svg { display: none !important; }'
      + 'table { border-collapse: collapse; width: 100%; font-size: 12px; }'
      + 'th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: center; }'
      + 'th { background: #0F766E; color: #fff; font-weight: 600; }'
      + 'td:nth-child(2), th:nth-child(2) { text-align: left; }'
      + 'tfoot td { font-weight: 700; background: #f1f5f9; }'
      + 'button { background: none; border: none; color: inherit; font: inherit; cursor: default; padding: 0; }'
      + '</style></head><body>'
      + '<h1>' + title + '</h1>'
      + '<p class="sub">' + roleLabel + ' report · ' + dateLabel + '</p>'
      + node.innerHTML
      + '<scr' + 'ipt>window.onload=function(){window.print();};window.onafterprint=function(){window.close();};</scr' + 'ipt>'
      + '</body></html>'
    );
    win.document.close();
  };

  const selectedLocName = (locationData || []).find((l) => String(l.LocationId) === String(selectedLocationID))?.LocationNickname;
  const activeChips = [
 
    { id: 'staff', label: `${selectedProviders.length} ${roleLabel.toLowerCase()}(s)`, icon: faUsers },
    ...(selectedLocationID && selectedLocationID !== '-1' && selectedLocName
      ? [{ id: 'loc', label: selectedLocName, icon: faLocationDot, onClear: () => setSelectedLocationID && setSelectedLocationID('-1') }]
      : []),
    ...(searchQuery ? [{ id: 'q', label: `“${searchQuery}”`, icon: faMagnifyingGlass, onClear: () => setSearchQuery('') }] : []),
  ];

  return (
    <div className="hv-page hv-page--dense ptd">

      {/* ── Header ─────────────────────────────────────────── */}
      <header className="hv-pagehead ptd-header">
        <div className="ptd-header-titles">
          <h1>Patient Tracker</h1>
          <p>Encounter, CPT &amp; unit productivity by provider, counselor &amp; nurse</p>
        </div>
        <div className="ptd-header-actions">
          
        
          <div className="ptd-export">
            <button type="button" className="hv-btn ptd-btn" onClick={() => setExportOpen((v) => !v)}>
              <FontAwesomeIcon icon={faDownload} /> Export
            </button>
            {exportOpen && (
              <div className="ptd-export-menu" onMouseLeave={() => setExportOpen(false)}>
                <button type="button" onClick={() => exportActive('pdf')}><FontAwesomeIcon icon={faFilePdf} /> PDF</button>
                <button type="button" onClick={() => exportActive('excel')}><FontAwesomeIcon icon={faFileExcel} /> Excel</button>
                <button type="button" onClick={() => exportActive('csv')}><FontAwesomeIcon icon={faFileCsv} /> CSV</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Sticky filter toolbar ──────────────────────────── */}
      <div className="hv-toolbar ptd-toolbar">
        <div className="ptd-field">
          <label>From</label>
          <DatePicker
            selected={startDate ? new Date(startDate) : null}
            onChange={handleStartDateChange}
            dateFormat="MM/dd/yyyy"
            className="hv-date ptd-date"
            placeholderText="MM/DD/YYYY"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            scrollableYearDropdown
            yearDropdownItemNumber={15}
            calendarClassName="ptd-cal"
            popperClassName="ptd-cal-pop"
            todayButton="Today"
          />
        </div>
        <div className="ptd-field">
          <label>To</label>
          <DatePicker
            selected={endDate ? new Date(endDate) : null}
            onChange={handleEndDateChange}
            dateFormat="MM/dd/yyyy"
            className="hv-date ptd-date"
            placeholderText="MM/DD/YYYY"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            scrollableYearDropdown
            yearDropdownItemNumber={15}
            calendarClassName="ptd-cal"
            popperClassName="ptd-cal-pop"
            todayButton="Today"
          />
        </div>
        <div className="ptd-field ptd-field-grow">
          <label>Search {roleLabel}</label>
          <div className="ptd-input-icon">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input type="text" value={searchQuery} onChange={handleSearchChange} placeholder={`Filter ${roleLabel.toLowerCase()} name…`} />
          </div>
        </div>
        <button type="button" className="hv-btn ptd-btn ptd-btn-primary" onClick={onApply}><FontAwesomeIcon icon={faMagnifyingGlass} /> Apply</button>
        <button type="button" className="hv-btn ptd-btn" onClick={handleReset}><FontAwesomeIcon icon={faRotate} /> Reset</button>
        <div className="ptd-chips">
          {activeChips.map((c) => (
            <span key={c.id} className="ptd-chip">
              <FontAwesomeIcon icon={c.icon} /> {c.label}
              {c.onClear && <button type="button" onClick={c.onClear}><FontAwesomeIcon icon={faXmark} /></button>}
            </span>
          ))}
        </div>
      </div>

      {/* ── KPI row ────────────────────────────────────────── */}
      <KpiGrid kpis={kpis} activeMetric={activeMetric} onPick={pickMetric} />

      {/* ── Two-column body ───────────────────────────────── */}
      <div className="ptd-body">
        <aside className="ptd-col-left">
          <StaffSelector
            roleLabel={roleLabel}
            providerData={providerData}
            selectedProviders={selectedProviders}
            setSelectedProviders={setSelectedProviders}
            handleCheckboxChange={handleCheckboxChange}
            locationData={locationData}
            selectedLocationID={selectedLocationID}
            onLocationChange={setSelectedLocationID}
          />
          <InsightsPanel rows={filteredTable1} roleLabel={roleLabel} />
        </aside>

        <main className="ptd-col-right">
          {/* Report tabs (Encounter / CPT / Units / Chart) */}
          <section className="hv-panel ptd-card ptd-reports">
            <div className="ptd-report-tabs">
              {REPORT_TABS.map((t) => (
                <button key={t.key} type="button" className={`ptd-rtab ${report === t.key ? 'active' : ''}`} onClick={() => setReport(t.key)}>{t.label}</button>
              ))}
              <div className="ptd-report-spacer" />
              {report !== 'chart' && (
                <div className="ptd-report-exports">
                  <button type="button" className="hv-btn ptd-btn ptd-btn-sm" onClick={printActive}><FontAwesomeIcon icon={faPrint} /> Print</button>
                  <button type="button" className="hv-btn ptd-btn ptd-btn-sm" onClick={() => exportActive('pdf')}><FontAwesomeIcon icon={faFilePdf} /> PDF</button>
                  <button type="button" className="hv-btn ptd-btn ptd-btn-sm" onClick={() => exportActive('csv')}><FontAwesomeIcon icon={faFileCsv} /> CSV</button>
                </div>
              )}
            </div>

            <div className="ptd-report-body">
              {loading ? <TableSkeleton /> : (
                <>
                  {report === 'encounter' && (
                    !hasRows ? <EmptyState label="encounter" /> : (
                      <div className="ptd-report-fill" ref={encounterTableRef}>
                        <EncounterTrackerTableV2
                          data={filteredTable1}
                          handleGetPatientTrackerDetailedData={handleGetPatientTrackerDetailedData}
                          handleGetUserAudits={handleGetUserAudits}
                          onRowOpen={(row) => setRowDrawer(row)}
                        />
                      </div>
                    )
                  )}
                  {report === 'cpt' && (
                    cptCodes.length === 0 ? <EmptyState label="CPT" /> : (
                      <GroupedGrid ref={sortedTable2Ref} groups={groupedCPTs} codes={cptCodes} rows={sortedTable2} totalsSource={CptTrackerData} accent="teal" search={searchQueryTable2} onSearch={handleTable2SearchChange} onSort={(code) => handleSort('table2', code)} sortConfig={sortConfigTable2} />
                    )
                  )}
                  {report === 'units' && (
                    unitCodes.length === 0 ? <EmptyState label="units" /> : (
                      <GroupedGrid ref={sortedTable3Ref} groups={groupedunits} codes={unitCodes} rows={sortedTable3} totalsSource={unitTrackerTable1} accent="teal" search={searchQueryTable3} onSearch={handleTable3SearchChange} onSort={(code) => handleSort('table3', code)} sortConfig={sortConfigTable3} />
                    )
                  )}
                  {report === 'chart' && (
                    !hasRows ? <EmptyState label="chart" /> : <TrackerChartV2 chartData={chartData} dateLabel={dateLabel} />
                  )}
                </>
              )}
            </div>
          </section>
        </main>
      </div>

      {/* ── Provider detail drawer (row click) ─────────────── */}
      <Drawer anchor="right" open={!!rowDrawer} onClose={() => setRowDrawer(null)} PaperProps={{ className: 'ptd-drawer' }}>
        {rowDrawer && (
          <div className="ptd-drawer-inner">
            <div className="ptd-drawer-head">
              <div className="ptd-drawer-id">
                <span className="ptd-avatar lg" style={{ background: getAvatarColor(rowDrawer.ProviderName) }}>{getInitials(rowDrawer.ProviderName)}</span>
                <div>
                  <h3>{getDisplayName(rowDrawer.ProviderName)}</h3>
                  <span className="ptd-badge">{getRoleBadge(rowDrawer.ProviderName, roleLabel)}</span>
                </div>
              </div>
              <button type="button" className="hv-icon-btn ptd-icon-btn" onClick={() => setRowDrawer(null)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div className="ptd-drawer-metrics">
              {[
                ['Total Patients', rowDrawer.nonduplicatePatcount, 2],
                ['Signed', rowDrawer.SignedEnc, 1],
                ['Unsigned', rowDrawer.UnsignedEnc, 0],
                ['Total Encounters', rowTotalEnc(rowDrawer), 3],
                ['Total CPTs', rowDrawer.CPTCount, null],
                ['Total Units', rowDrawer.UnitCount, null],
              ].map(([label, val, ct]) => (
                <button
                  key={label}
                  type="button"
                  className={`ptd-drawer-metric ${ct !== null ? 'clickable' : ''}`}
                  onClick={() => { if (ct !== null) { handleGetPatientTrackerDetailedData(rowDrawer, ct); } }}
                >
                  <span className="v">{formatNumber(val)}</span>
                  <span className="l">{label}{ct !== null && <FontAwesomeIcon icon={faChevronRight} />}</span>
                </button>
              ))}
            </div>
            <p className="ptd-drawer-hint">Click a metric with <FontAwesomeIcon icon={faChevronRight} /> to view the patient / encounter breakdown.</p>
          </div>
        )}
      </Drawer>

      {/* ── Breakdown drawer (encounter / patient lists) ───── */}
      <Drawer anchor="right" open={breakdownOpen} onClose={handleCloseModals} PaperProps={{ className: 'ptd-drawer wide' }}>
        <div className="ptd-drawer-inner">
          <div className="ptd-drawer-head">
            <h3>{showDataModal ? 'Patient Breakdown' : 'Encounter Breakdown'}</h3>
            <div className="ptd-drawer-tools">
              <Tooltip title="Excel" arrow><button type="button" className="hv-icon-btn ptd-icon-btn" onClick={() => exportEncToExcel('patientTracker', 'patientTracker')}><FontAwesomeIcon icon={faFileExcel} /></button></Tooltip>
              <Tooltip title="PDF" arrow><button type="button" className="hv-icon-btn ptd-icon-btn" onClick={() => exportToPDF('patientTracker', 'patientTracker')}><FontAwesomeIcon icon={faFilePdf} /></button></Tooltip>
              <button type="button" className="hv-icon-btn ptd-icon-btn" onClick={handleCloseModals}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
          </div>
          <div className="hv-table-scroll ptd-grid-wrap" ref={patientTableRef} id="patientTracker">
            <table className="hv-table hv-table--compact hv-table--striped ptd-grid accent-teal">
              <thead>
                {showDataModal ? (
                  <tr><th className="ptd-grid-idx">#</th><th className="ptd-grid-name">Patient Name</th></tr>
                ) : (
                  <tr>
                    <th className="ptd-grid-idx">#</th><th>DOS</th><th className="ptd-grid-name">Patient</th>
                    <th>Provider</th><th>Supervisor</th><th>Signed Off</th><th>Form</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {showDataModal
                  ? (filteredEncPatData || []).map((item, i) => (
                    <tr key={i}>
                      <td className="ptd-grid-idx">{i + 1}</td>
                      <td className="ptd-grid-name"><button type="button" className="ptv2-mt-link" onClick={() => handlePatLinkClick(item)}>{item.LastName}, {item.FirstName}</button>
                     
                        <span> <FontAwesomeIcon style={{ marginLeft: '20px', color: '#000', fontWeight: '100', float:'right' }} onClick={() => handleGetEncounterAudits(item)} icon={faFile} /></span>
                      </td>
                      
                    </tr>
                  ))
                  : (filteredEncData || []).map((item, i) => (
                    <tr key={i}>
                      <td className="ptd-grid-idx">{i + 1}</td>
                      <td>{item.DOSString}</td>
                      <td className="ptd-grid-name">
                        <button type="button" className="ptv2-mt-link" onClick={() => handlePatLinkClick(item)}>{item.LastName}, {item.FirstName}</button>
                        <FontAwesomeIcon icon={faPrint} className="ptv2-mt-audit" style={{ marginLeft: 8 }} onClick={() => handleGetEncounterAudits(item)} />
                      </td>
                      <td>{item.ProviderName}</td>
                      <td>{item.SupProviderName}</td>
                      <td><span className="ptd-pill">{getSignedOffMsg(item.EncounterStatus, item.AssistantSignoff)}</span></td>
                      <td>{item.FormName}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </Drawer>

      {/* ── Audit drawer ───────────────────────────────────── */}
      <Drawer anchor="right" open={showAuditModal} onClose={handleAuditCloseModals} PaperProps={{ className: 'ptd-drawer' }}>
        <div className="ptd-drawer-inner">
          <div className="ptd-drawer-head">
            <h3>Audit Trail</h3>
            <button type="button" className="hv-icon-btn ptd-icon-btn" onClick={handleAuditCloseModals}><FontAwesomeIcon icon={faXmark} /></button>
          </div>
          <div className="hv-table-scroll ptd-grid-wrap">
            <table className="hv-table hv-table--compact hv-table--striped ptd-grid accent-teal">
              <thead><tr><th className="ptd-grid-idx">#</th><th className="ptd-grid-name">Description</th><th>Date</th></tr></thead>
              <tbody>
                {(filteredAuditData || []).map((item, i) => (
                  <tr key={i}><td className="ptd-grid-idx">{i + 1}</td><td className="ptd-grid-name">{item.AuditDescription}</td><td>{formatDOSDate(item.AuditDate)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default TrackerDashboardV2;
